import { useCallback, useEffect, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';

import { Row, Col, Flex, Card, Typography, Button, Tag, Skeleton } from 'antd';
import { FilterOutlined } from '@ant-design/icons';

import { AnalyticsCard } from './AnalyticsCard';
import { AnalyticsCardDateFilter } from './AnalyticsCardDateFilter';
import dayjs, { Dayjs } from 'dayjs';
import { AnalyticsInfoCard } from './AnalyticsInfoCard';

import './styles.scss';

import { debounce } from 'lodash';
import type {
  AdditionalMetricsResponse,
  PartipicationChartItem,
  PartipicationResponse,
  RevenueChartItem,
  RevenueResponse,
} from '../../interface/campaign-analytics';
import type { CampaignDetailInterface, CampaignMetricsRangeInterface } from '../../interface/campaign';
import sendRequest from '../../services/service';
import { formatDate } from '../../helpers/mixins';
import { useReportAuth } from '../../hooks/useReportAuth';
import PageWrapper from '../../components/PageWrapper';
import { isKahveDiyariDomain } from '../../helpers/domainUtils';
import FilterDrawer from './FilterModal';

// Ortam bazlı ID yapılandırması
const ENVIRONMENT_CONFIG = {
  whitelabel: {
    applicationId: 'gh6GWKAVNN1eF1_-atbuwA==',
    campaignId: 'B2KAKetafBPqK40SF-PFcw==',
  },
  kahveDiyari: {
    applicationId: 'pIsvirxheLBhVT8qsQTu5A==',
    campaignId: 'BNIxXwFJ34wquGnELbXewg==',
  },
};

// Aktif ortama göre ID'leri seç
const getEnvironmentConfig = () => {
  return isKahveDiyariDomain() ? ENVIRONMENT_CONFIG.kahveDiyari : ENVIRONMENT_CONFIG.whitelabel;
};

const APPLICATION_ID = getEnvironmentConfig().applicationId;

export default function CampaignAnalytics() {
  const navigate = useNavigate();
  const params = useParams();
  const campaignId = params.campaignId;
  const outputFormat = 'YYYY-MM-DD';

  const [showFilters, setShowFilters] = useState(false);
  const [filterQuery, setFilterQuery] = useState<{ attributeId: string; value: string } | null>(null);

  // Report Authentication hook'u
  const { data: reportAuth, isLoading: reportAuthLoading, error: reportAuthError } = useReportAuth();

  const paritipicationChartOptions = {
    paritipicationUniqueCustomerId: 'Unique Customer',
    paritipicationUniqueCustomerColor: '#E6AA38',
    paritipicationTotalParticipantsId: 'Total Participants',
    paritipicationTotalParticipantsColor: '#00918E',
  };

  const revenueChartOptions = {
    revenueChartRevenueId: 'Total Revenue',
    revenueChartRevenueColor: '#F37761',
    revenueChartBasketAmountId: 'Total Basket Amount',
    revenueChartBasketAmountColor: '#00918E',
    revenueChartDiscountAmountId: 'Total Discount Amount',
    revenueChartDiscountAmountColor: '#E6AA38',
  };

  const last7Days = dayjs().subtract(6, 'day');

  const [loadingStatus, setLoadingStatus] = useState<boolean>(false);
  const [emptyStateStatus, setEmptyStateStatus] = useState<boolean>(false);

  const [partipicationLoading, setPartipicationLoading] = useState<boolean>(true);
  const [partipicationInterval, setPartipicationInterval] = useState<string>('Daily');
  const [partipicationFilteredDates, setPartipicationFilteredDates] = useState<[Dayjs | null, Dayjs | null]>([last7Days, dayjs()]);
  const [partipicationDates, setPartipicationDates] = useState<[Dayjs | null, Dayjs | null]>([last7Days, dayjs()]);
  const [partipicationLegendUniqueConsumerChecked, setPartipicationLegendUniqueConsumerChecked] = useState<boolean>(true);
  const [partipicationLegendTotalParticipationChecked, setPartipicationLegendTotalParticipationChecked] = useState<boolean>(true);
  const [partipicationData, setPartipicationData] = useState<PartipicationResponse>();

  const [revenueLoading, setRevenueLoading] = useState<boolean>(true);
  const [revenueInterval, setRevenueInterval] = useState<string>('Daily');
  const [revenueFilteredDates, setRevenueFilteredDates] = useState<[Dayjs | null, Dayjs | null]>([last7Days, dayjs()]);
  const [revenueDates, setRevenueDates] = useState<[Dayjs | null, Dayjs | null]>([last7Days, dayjs()]);
  const [revenueLegendRevenueChecked, setRevenueLegendRevenueChecked] = useState<boolean>(true);
  const [revenueLegendBasketAmountChecked, setRevenueLegendBasketAmountChecked] = useState<boolean>(true);
  const [revenueLegendDiscountAmountChecked, setRevenueLegendDiscountAmountChecked] = useState<boolean>(true);
  const [revenueData, setRevenueData] = useState<RevenueResponse>();

  const [additionalMetricsLoading, setAdditionalMetricsLoading] = useState<boolean>(true);
  const [additionalMetricsFilteredDates, setAdditionalMetricsFilteredDates] = useState<[Dayjs | null, Dayjs | null]>([last7Days, dayjs()]);
  const [additionalMetricsDates, setAdditionalMetricsDates] = useState<[Dayjs | null, Dayjs | null]>([last7Days, dayjs()]);
  const [additionalMetricsData, setAdditionalMetricsData] = useState<AdditionalMetricsResponse>();

  const [campaign, setCampaign] = useState<CampaignDetailInterface>();
  const [campaignMetricsRange, setCampaignMetricsRange] = useState<CampaignMetricsRangeInterface>();
  const [currency, setCurrency] = useState<string | null>(null);
  const [notTransaction, setNotTransaction] = useState<boolean>(false);
  const [applicationTimezone, setApplicationTimezone] = useState<string | null>(null);
  useEffect(() => {
    if (!campaignId) {
      console.error('Campaign ID is missing from URL');
      return;
    }

    // Report auth loading durumunu kontrol et
    if (reportAuthLoading) return;

    // Report auth hata kontrolü
    if (reportAuthError) {
      console.error('Report authentication failed:', reportAuthError);
      return;
    }

    // Access token kontrolü
    if (!reportAuth?.accessToken) {
      console.error('No access token available for reports');
      return;
    }

    (async () => {
      setLoadingStatus(true);
      await fetchApplication();
      const campaign = await fetchCampaign();
      const ruleExists = campaign?.rules?.length > 0;
      const campaignMetricsRange = await fetchCampaignMatricsRange();
      if (!ruleExists && !campaignMetricsRange?.startDate) {
        setEmptyStateStatus(true);
      } else {
        setEmptyStateStatus(false);
      }

      if (ruleExists && !campaignMetricsRange?.startDate) {
        setAdditionalMetricsLoading(false);
        setRevenueLoading(false);
        setPartipicationLoading(false);
        setNotTransaction(true);
      }

      setLoadingStatus(false);
    })();
  }, [campaignId, reportAuth, reportAuthLoading, reportAuthError]);

  useEffect(() => {
    if (!campaign || !campaignMetricsRange?.startDate) return;
    debouncedFetchParticipationMetrics(filterQuery);

    return () => {
      debouncedFetchParticipationMetrics.cancel();
    };
  }, [partipicationFilteredDates, partipicationInterval, campaign, campaignMetricsRange, filterQuery]);

  useEffect(() => {
    if (!campaign || !campaignMetricsRange?.startDate) return;
    debouncedFetchRevenueMetrics(filterQuery);

    return () => {
      debouncedFetchRevenueMetrics.cancel();
    };
  }, [revenueFilteredDates, revenueInterval, campaign, campaignMetricsRange, filterQuery]);

  useEffect(() => {
    if (!campaign || !campaignMetricsRange?.startDate) return;
    debouncedFetchAdditionalMetrics(filterQuery);

    return () => {
      debouncedFetchAdditionalMetrics.cancel();
    };
  }, [additionalMetricsFilteredDates, campaign, campaignMetricsRange, filterQuery]);

  const parsedRevenueChartData = (items: RevenueChartItem[]) => {
    return [
      {
        id: revenueChartOptions.revenueChartRevenueId,
        color: revenueChartOptions.revenueChartRevenueColor,
        data: items?.map((item) => ({
          x: new Date(item.date),
          y: item.revenue,
        })),
      },
      {
        id: revenueChartOptions.revenueChartBasketAmountId,
        color: revenueChartOptions.revenueChartBasketAmountColor,
        data: items?.map((item) => ({
          x: new Date(item.date),
          y: item.basketAmount,
        })),
      },
      {
        id: revenueChartOptions.revenueChartDiscountAmountId,
        color: revenueChartOptions.revenueChartDiscountAmountColor,
        data: items?.map((item) => ({
          x: new Date(item.date),
          y: item.discountAmount,
        })),
      },
    ];
  };

  const parsedParticipationChartData = (items: PartipicationChartItem[]) => {
    return [
      {
        id: paritipicationChartOptions.paritipicationUniqueCustomerId,
        color: paritipicationChartOptions.paritipicationUniqueCustomerColor,
        data: items?.map((item) => ({
          x: new Date(item.date),
          y: item.totalUniqueParticipations,
        })),
      },
      {
        id: paritipicationChartOptions.paritipicationTotalParticipantsId,
        color: paritipicationChartOptions.paritipicationTotalParticipantsColor,
        data: items?.map((item) => ({
          x: new Date(item.date),
          y: item.totalParticipations,
        })),
      },
    ];
  };

  const renderCampaignStateTag = (state: string) => {
    if (state === 'Running') return <Tag color="green">Running</Tag>;
    if (state === 'Scheduled') return <Tag color="orange">Scheduled</Tag>;
    if (state === 'Draft') return <Tag color="blue">Draft</Tag>;
    if (state === 'Expired') return <Tag color="red">Expired</Tag>;
    if (state === 'Disabled') return <Tag color="gray">Disabled</Tag>;
    return null;
  };

  const fetchCampaign = async () => {
    if (!campaignId) return;
    try {
      const response = await sendRequest.get({
        url: `/Campaigns/${campaignId}`,
        reportAccessToken: reportAuth?.accessToken,
        skipUrlOrganizationId: true,
      });
      setCampaign(response);
      return response;
    } catch (error) {
      console.log(error);
    }
  };

  const fetchApplication = async () => {
    try {
      const response = await sendRequest.get({
        url: `/Applications/${APPLICATION_ID}`,
        reportAccessToken: reportAuth?.accessToken,
        skipUrlOrganizationId: true,
      });
      setCurrency(response.currencyType);
      setApplicationTimezone(response.timeZoneType);
    } catch (error) {
      console.log(error);
    }
  };

  const fetchCampaignMatricsRange = async () => {
    if (!campaignId) return;
    try {
      const response = await sendRequest.get({
        url: `/CampaignReports/metricsrange`,
        baseURL: '/api/v1/transaction',
        data: {
          CampaignId: campaignId,
        },
        reportAccessToken: reportAuth?.accessToken,
        skipUrlOrganizationId: true,
      });
      setCampaignMetricsRange(response);
      return response;
    } catch (error) {
      console.error('fetchCampaignMatricsRange error:', error);
      // Hata durumunda boş bir obje döndür
      const emptyResponse = { startDate: '', endDate: '' };
      setCampaignMetricsRange(emptyResponse);
      return emptyResponse;
    }
  };

  const debouncedFetchParticipationMetrics = useCallback(
    debounce(async (filterQuery: { attributeId: string; value: string } | null) => {
      if (!campaignId) return;
      try {
        setPartipicationLoading(true);
        const response = await sendRequest.get({
          url: `/CampaignReports/participationmetrics`,
          baseURL: '/api/v1/transaction',
          data: {
            CampaignId: campaignId,
            StartDate: partipicationFilteredDates[0]?.format(outputFormat) + 'T00:00:00',
            EndDate: partipicationFilteredDates[1]?.format(outputFormat) + 'T23:59:59',
            Interval: partipicationInterval,
            ...(filterQuery && { [`Filter.Attributes[${filterQuery.attributeId}]`]: filterQuery.value }),
          },
          reportAccessToken: reportAuth?.accessToken,
          skipUrlOrganizationId: true,
        });
        const parsedParticipationResponse = {
          ...response,
          items: parsedParticipationChartData(response.items),
        };

        setPartipicationData(parsedParticipationResponse);
      } catch (error) {
        console.log(error);
      } finally {
        setPartipicationLoading(false);
      }
    }, 500), // 500ms debounce süresi
    [campaignId, partipicationFilteredDates, partipicationInterval, reportAuth?.accessToken]
  );

  const debouncedFetchRevenueMetrics = useCallback(
    debounce(async (filterQuery: { attributeId: string; value: string } | null) => {
      if (!campaignId) return;
      try {
        setRevenueLoading(true);
        const response = await sendRequest.get({
          url: `/CampaignReports/revenuemetrics`,
          baseURL: '/api/v1/transaction',
          data: {
            CampaignId: campaignId,
            StartDate: revenueFilteredDates[0]?.format(outputFormat) + 'T00:00:00',
            EndDate: revenueFilteredDates[1]?.format(outputFormat) + 'T23:59:59',
            Interval: revenueInterval,
            ...(filterQuery && { [`Filter.Attributes[${filterQuery.attributeId}]`]: filterQuery.value }),
          },
          reportAccessToken: reportAuth?.accessToken,
          skipUrlOrganizationId: true,
        });
        const parsedRevenueResponse = {
          ...response,
          items: parsedRevenueChartData(response.items),
        };

        setRevenueData(parsedRevenueResponse);
      } catch (error) {
        console.log(error);
      } finally {
        setRevenueLoading(false);
      }
    }, 500), // 500ms debounce süresi
    [campaignId, revenueFilteredDates, revenueInterval, reportAuth?.accessToken]
  );

  const debouncedFetchAdditionalMetrics = useCallback(
    debounce(async (filterQuery: { attributeId: string; value: string } | null) => {
      if (!campaignId) return;
      try {
        setAdditionalMetricsLoading(true);
        const response = await sendRequest.get({
          url: `/CampaignReports/additionalmetrics`,
          baseURL: '/api/v1/transaction',
          data: {
            CampaignId: campaignId,
            StartDate: additionalMetricsFilteredDates[0]?.format(outputFormat) + 'T00:00:00',
            EndDate: additionalMetricsFilteredDates[1]?.format(outputFormat) + 'T23:59:59',
            ...(filterQuery && { [`Filter.Attributes[${filterQuery.attributeId}]`]: filterQuery.value }),
          },
          reportAccessToken: reportAuth?.accessToken,
          skipUrlOrganizationId: true,
        });
        setAdditionalMetricsData(response);
      } catch (error) {
        console.log(error);
      } finally {
        setAdditionalMetricsLoading(false);
      }
    }, 500), // 500ms debounce süresi
    [campaignId, additionalMetricsFilteredDates, reportAuth?.accessToken]
  );

  const EmptyState = () => {
    return (
      <Card className="custom-card child-card">
        <Flex
          vertical
          gap={30}
          align={'center'}
          justify={'center'}
        >
          <Flex
            gap={5}
            align={'center'}
            justify={'center'}
            vertical
          >
            <Typography.Title level={4}>Insights will appear once your campaign has rules or transaction.</Typography.Title>
            <Typography.Text type="secondary">Click "Create Rule" button to build rules</Typography.Text>
          </Flex>
          <Button
            color="cyan"
            variant="solid"
            onClick={() => navigate(`/applications/${params.appId}/campaigns/${params.campaignId}/rules`)}
          >
            Create Rule
          </Button>
        </Flex>
      </Card>
    );
  };

  const LoadingSkeleton = () => {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Row>
            <Col span={24}>
              <Flex
                gap={16}
                vertical
              >
                <Card className="custom-card child-card">
                  <Skeleton active />
                </Card>
                <Card className="custom-card child-card">
                  <Skeleton active />
                </Card>
                <Card className="custom-card child-card">
                  <Skeleton active />
                </Card>
              </Flex>
            </Col>
          </Row>
        </div>
      </div>
    );
  };

  if (!campaignId) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Row>
            <Col span={24}>
              <Card className="custom-card child-card">
                <Flex
                  vertical
                  gap={20}
                  align="center"
                  justify="center"
                >
                  <Typography.Title
                    level={4}
                    type="danger"
                  >
                    Campaign ID Missing
                  </Typography.Title>
                  <Typography.Text type="secondary">Please select a campaign from the sidebar to view analytics.</Typography.Text>
                </Flex>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  // Report auth error durumunda error göster
  if (reportAuthError) {
    return (
      <div className="page-wrapper">
        <div className="content">
          <Row>
            <Col span={24}>
              <Card className="custom-card child-card">
                <Flex
                  vertical
                  gap={20}
                  align="center"
                  justify="center"
                >
                  <Typography.Title
                    level={4}
                    type="danger"
                  >
                    Report Authentication Failed
                  </Typography.Title>
                  <Typography.Text type="secondary">Unable to authenticate for report access. Please try again.</Typography.Text>
                </Flex>
              </Card>
            </Col>
          </Row>
        </div>
      </div>
    );
  }

  // Loading durumlarını kontrol et

  return (
    <PageWrapper
      title="Campaign Reports"
      actionButtons={[
        {
          title: 'Filter',
          icon: <FilterOutlined />,
          permission: true,
          theme: 'secondary',
          onClick: () => setShowFilters(true),
          hasActiveFilters: !!filterQuery,
        },
      ]}
    >
      {!emptyStateStatus && (
        <Flex
          vertical
          gap={24}
        >
          <AnalyticsCard
            chartData={
              partipicationData && partipicationData?.items.length > 0
                ? partipicationData?.items.filter(
                    (item) =>
                      (item.id === paritipicationChartOptions.paritipicationUniqueCustomerId && partipicationLegendUniqueConsumerChecked) ||
                      (item.id === paritipicationChartOptions.paritipicationTotalParticipantsId && partipicationLegendTotalParticipationChecked)
                  )
                : []
            }
            dates={partipicationDates}
            interval={partipicationInterval}
            timezone={applicationTimezone || ''}
            title="Participation Analytics"
            loading={partipicationLoading}
            extra={
              <AnalyticsCardDateFilter
                pickerStartInputId="participation-start"
                pickerEndInputId="participation-end"
                filteredDates={partipicationFilteredDates}
                setFilteredDates={setPartipicationFilteredDates}
                dates={partipicationDates}
                setDates={setPartipicationDates}
                interval={partipicationInterval}
                setInterval={setPartipicationInterval}
                minDate={campaignMetricsRange?.startDate}
                notTransaction={notTransaction}
              />
            }
          >
            <AnalyticsInfoCard
              title="Unique Consumer"
              isLegend={true}
              value={partipicationData?.totalUniqueParticipations?.toString() || '-'}
              loading={partipicationLoading}
              checkedValue={partipicationLegendUniqueConsumerChecked}
              onChange={setPartipicationLegendUniqueConsumerChecked}
              tooltipDescription="Total unique consumer who successfully participated"
              notTransaction={notTransaction}
            />
            <AnalyticsInfoCard
              title="Total Participation"
              isLegend={true}
              value={partipicationData?.totalParticipations?.toString() || '-'}
              loading={partipicationLoading}
              checkedValue={partipicationLegendTotalParticipationChecked}
              onChange={setPartipicationLegendTotalParticipationChecked}
              tooltipDescription="Total number of participations in the campaign"
              notTransaction={notTransaction}
            />
            <AnalyticsInfoCard
              title="Avr. Participation Rate"
              isLegend={false}
              value={partipicationData?.averageParticipationRate?.toString() || '-'}
              loading={partipicationLoading}
              tooltipDescription="Average campaign participation per consumer"
              notTransaction={notTransaction}
            />
          </AnalyticsCard>
          <AnalyticsCard
            chartData={
              revenueData && revenueData?.items.length > 0
                ? revenueData?.items.filter(
                    (item) =>
                      (item.id === revenueChartOptions.revenueChartRevenueId && revenueLegendRevenueChecked) ||
                      (item.id === revenueChartOptions.revenueChartBasketAmountId && revenueLegendBasketAmountChecked) ||
                      (item.id === revenueChartOptions.revenueChartDiscountAmountId && revenueLegendDiscountAmountChecked)
                  )
                : []
            }
            title="Revenue Analytics"
            timezone={applicationTimezone || ''}
            dates={revenueDates}
            interval={revenueInterval}
            loading={revenueLoading}
            extra={
              <AnalyticsCardDateFilter
                pickerStartInputId="revenue"
                pickerEndInputId="revenue-end"
                filteredDates={revenueFilteredDates}
                setFilteredDates={setRevenueFilteredDates}
                dates={revenueDates}
                setDates={setRevenueDates}
                interval={revenueInterval}
                setInterval={setRevenueInterval}
                minDate={campaignMetricsRange?.startDate}
                notTransaction={notTransaction}
              />
            }
          >
            <AnalyticsInfoCard
              title="Total Basket Amount"
              isLegend={true}
              value={revenueData?.totalBasketAmount?.toString() || '-'}
              checkedValue={revenueLegendBasketAmountChecked}
              onChange={setRevenueLegendBasketAmountChecked}
              loading={revenueLoading}
              currency={currency || ''}
              tooltipDescription="Total basket amount in the closed sessions"
              notTransaction={notTransaction}
            />
            <AnalyticsInfoCard
              title="Total Discount Amount"
              isLegend={true}
              value={revenueData?.totalDiscountAmount?.toString() || '-'}
              checkedValue={revenueLegendDiscountAmountChecked}
              onChange={setRevenueLegendDiscountAmountChecked}
              loading={revenueLoading}
              currency={currency || ''}
              tooltipDescription="Total basket discount amount in the closed sessions"
              notTransaction={notTransaction}
            />
            <AnalyticsInfoCard
              title="Total Revenue"
              isLegend={true}
              value={revenueData?.totalRevenue?.toString() || '-'}
              checkedValue={revenueLegendRevenueChecked}
              onChange={setRevenueLegendRevenueChecked}
              loading={revenueLoading}
              currency={currency || ''}
              tooltipDescription="(Total Amount Spent - Total Discounts) in the closed sessions"
              notTransaction={notTransaction}
            />
            <AnalyticsInfoCard
              title="Avr. Basket Amount"
              isLegend={false}
              list={[
                {
                  title: 'Per consumer',
                  value: revenueData?.averageBasketAmountPerConsumer?.toString() || '-',
                },
                {
                  title: 'Per session',
                  value: revenueData?.averageBasketAmountPerSession?.toString() || '-',
                },
              ]}
              loading={revenueLoading}
              currency={currency || ''}
              tooltipDescription="Average basket amount per consumer and per session"
              notTransaction={notTransaction}
            />
            <AnalyticsInfoCard
              title="Avr. Discount Amount"
              isLegend={false}
              list={[
                {
                  title: 'Per consumer',
                  value: revenueData?.averageDiscountAmountPerConsumer?.toString() || '-',
                },
                {
                  title: 'Per session',
                  value: revenueData?.averageDiscountAmountPerSession?.toString() || '-',
                },
              ]}
              loading={revenueLoading}
              currency={currency || ''}
              tooltipDescription="Average basket discount amount per consumer and per session"
              notTransaction={notTransaction}
            />
          </AnalyticsCard>
          <AnalyticsCard
            title="Additional Metrics"
            timezone={applicationTimezone || ''}
            hideChart={true} // Hide chart area completely for metrics-only view
            extra={
              <AnalyticsCardDateFilter
                pickerStartInputId="additional-metrics"
                pickerEndInputId="additional-metrics-end"
                filteredDates={additionalMetricsFilteredDates}
                setFilteredDates={setAdditionalMetricsFilteredDates}
                dates={additionalMetricsDates}
                setDates={setAdditionalMetricsDates}
                minDate={campaignMetricsRange?.startDate}
                notTransaction={notTransaction}
              />
            }
          >
            <AnalyticsInfoCard
              title="Unique User Who Earn Points"
              isLegend={false}
              value={additionalMetricsData?.loyaltyPointGivenUniqueConsumerCount?.toString() || '-'}
              isMetric={true}
              loading={additionalMetricsLoading}
              tooltipDescription="Number of unique consumers who earn points in the campaign"
              notTransaction={notTransaction}
            />
            <AnalyticsInfoCard
              title="Awarded Loyalty Points"
              isLegend={false}
              value={additionalMetricsData?.loyaltyPointAwardedPoints?.toString() || '-'}
              isMetric={true}
              loading={additionalMetricsLoading}
              tooltipDescription="The total loyalty points awarded in the campaign"
              notTransaction={notTransaction}
            />
            <AnalyticsInfoCard
              title="Redeemed Loyalty Points"
              isLegend={false}
              value={additionalMetricsData?.loyaltyPointRedeemedPoints?.toString() || '-'}
              isMetric={true}
              loading={additionalMetricsLoading}
              tooltipDescription="The total loyalty points redeemed in the campaign"
              notTransaction={notTransaction}
            />
          </AnalyticsCard>
        </Flex>
      )}
      <FilterDrawer
        open={showFilters}
        onClose={() => setShowFilters(false)}
        onFilter={(query: { attributeId: string; value: string }) => {
          setFilterQuery(query.attributeId !== '' && query.value !== '' ? { attributeId: query.attributeId, value: query.value } : null);
        }}
        appId={APPLICATION_ID}
      />
    </PageWrapper>
  );
}
